import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { usePeriods } from '@/services/usePeriods';
﻿import { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, Users, Loader2, Search , ChevronLeft, ChevronRight } from "lucide-react";
import { useAdminConferenceSpeakers, useCreateConferenceSpeaker, useUpdateConferenceSpeaker, useDeleteConferenceSpeaker, type ConferenceSpeaker } from "@/services/adminConferenceSpeakerService";
import { useAdminCertificateTypes, type CertificateType } from "@/services/adminCertificateTypeService";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import toast from "react-hot-toast";

export default function SpeakersPage() {
    const { data: periods } = usePeriods();
    const activePeriod = periods?.find((p: any) => p.is_active);
    const [filterPeriod, setFilterPeriod] = useState<string>("all");
    const [filterConference, setFilterConference] = useState<string>("all");

    useEffect(() => {
        if (activePeriod && filterPeriod === "all") {
            setFilterPeriod(String(activePeriod.period_id));
        }
    }, [activePeriod, filterPeriod]);

    const { data: conferences } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conference-billing/accessible-conferences");
            return data.data;
        }
    });
    const [filterSearch, setFilterSearch] = useState<string>("");
    const [activeTab, setActiveTab] = useState<"speaker" | "moderator">("speaker");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // Fetch Data
    const { data: speakers, isLoading } = useAdminConferenceSpeakers(
        filterPeriod !== "all" ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : ""),
        filterConference !== "all" ? filterConference : undefined
    );
    const { data: certificateTypes, isLoading: isTypesLoading } = useAdminCertificateTypes();

    // Filter Data Local
    const filteredSpeakers = speakers?.filter((speaker: ConferenceSpeaker) => {
        if (activeTab === "speaker") {
            if (Number(speaker.certificate_type_id) === 7) return false;
        } else if (activeTab === "moderator") {
            if (Number(speaker.certificate_type_id) !== 7) return false;
        }

        if (!filterSearch) return true;
        const s = filterSearch.toLowerCase();
        return (
            (speaker.name || "").toLowerCase().includes(s) ||
            (speaker.topic || "").toLowerCase().includes(s) ||
            (speaker.institution || "").toLowerCase().includes(s)
        );
    }) || [];


    // Pagination Logic
    const totalItems = filteredSpeakers.length;
    const totalPages = Math.ceil(totalItems / perPage);
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const currentSpeakers = filteredSpeakers.slice(startIndex, endIndex);
    // Mutations
    const createSpeaker = useCreateConferenceSpeaker();
    const updateSpeaker = useUpdateConferenceSpeaker();
    const deleteSpeaker = useDeleteConferenceSpeaker();

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        period_id: "",
        conference_id: "",
        certificate_type_id: "none",
        name: "",
        email: "",
        topic: "",
        institution: "",
        is_present: false
    });

    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    const handleOpenModal = (speaker?: ConferenceSpeaker) => {
        if (speaker) {
            setEditingId(speaker.id);
            setFormData({
            period_id: speaker?.period_id ? String(speaker.period_id) : (filterPeriod !== "all" ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : "")),
            conference_id: speaker?.conference_id ? String(speaker.conference_id) : "none",
                certificate_type_id: speaker.certificate_type_id ? String(speaker.certificate_type_id) : "none",
                name: speaker.name || "",
                email: speaker.email || "",
                topic: speaker.topic || "",
                institution: speaker.institution || "",
                is_present: speaker.is_present
            });
        } else {
            setEditingId(null);
            setFormData({
                period_id: filterPeriod !== "all" ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : ""),
                conference_id: "none",
                certificate_type_id: "none",
                name: "",
                email: "",
                topic: "",
                institution: "",
                is_present: false
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name) {
            toast.error("Nama pembicara wajib diisi.");
            return;
        }

        const payload = {
            period_id: Number(formData.period_id),
            conference_id: formData.conference_id && formData.conference_id !== "none" ? Number(formData.conference_id) : null,
            certificate_type_id: formData.certificate_type_id !== "none" ? parseInt(formData.certificate_type_id) : null,
            name: formData.name,
            email: formData.email || null,
            topic: formData.topic,
            institution: formData.institution,
            is_present: formData.is_present
        };

        try {
            if (editingId) {
                await updateSpeaker.mutateAsync({ id: editingId, payload });
                toast.success("Data pembicara berhasil diperbarui!");
            } else {
                await createSpeaker.mutateAsync(payload);
                toast.success("Pembicara berhasil ditambahkan!");
            }
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
        }
    };

    const handleTogglePresent = async (id: number, currentVal: boolean) => {
        try {
            await updateSpeaker.mutateAsync({ id, payload: { is_present: !currentVal } });
            toast.success("Status kehadiran pembicara diperbarui.");
        } catch (error) {
            toast.error("Gagal memperbarui status kehadiran.");
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* Header Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <Users className="w-64 h-64 text-indigo-600" />
                </div>

                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 flex-shrink-0 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
                        <Users className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                            Pembicara Eksternal
                        </h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Kelola data pembicara/speaker (Keynote, Invited) yang bukan partisipan terdaftar.
                        </p>
                    </div>
                </div>

                <Button 
                    onClick={() => handleOpenModal()} 
                    className="relative z-10 h-12 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-600/20"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Tambah Pembicara
                </Button>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                {/* Search */}
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Select value={filterPeriod} onValueChange={(v) => { setFilterPeriod(v); setPage(1); }}>
                        <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                            <SelectValue placeholder="Pilih Periode" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                            <SelectItem value="all">Semua Periode</SelectItem>
                            {periods?.map((period: any) => (
                                <SelectItem key={period.period_id} value={String(period.period_id)}>
                                    {period.name} {period.is_active && "(Aktif)"}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filterConference} onValueChange={(v) => { setFilterConference(v); setPage(1); }}>
                        <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-left">
                            <div className="truncate w-full text-left min-w-0">
                                <SelectValue placeholder="Pilih Track Konferensi (Kosongkan jika Keynote)" />
                            </div>
                        </SelectTrigger>
                        <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                            <SelectItem value="all">Semua Track</SelectItem>
                            <SelectItem value="none">Utama (Keynote Speaker)</SelectItem>
                            {conferences?.map((conf: any) => (
                                <SelectItem key={conf.conference_id} value={String(conf.conference_id)} className="whitespace-normal break-words">
                                    {conf.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <div className="relative w-full">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <Input
                            type="text"
                            placeholder="Cari pembicara..."
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(1); }}
                            className="pl-10 h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl w-full"
                        />
                    </div>
                </div>

                                <div className="px-5 md:px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                    <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val as "speaker" | "moderator"); setPage(1); }} className="w-full">
                        <TabsList className="grid w-full max-w-md grid-cols-2">
                            <TabsTrigger value="speaker">Speaker (Keynote/Invited)</TabsTrigger>
                            <TabsTrigger value="moderator">Moderator</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold w-16 text-center">No</th>
                                {activeTab === "speaker" ? (
                                    <>
                                        <th className="px-6 py-4 font-semibold">Nama Pembicara</th>
                                        <th className="px-6 py-4 font-semibold">Topik & Institusi</th>
                                    </>
                                ) : (
                                    <>
                                        <th className="px-6 py-4 font-semibold">Nama Moderator</th>
                                        <th className="px-6 py-4 font-semibold">Ruangan</th>
                                    </>
                                )}
                                <th className="px-6 py-4 font-semibold text-center w-28">Hadir</th>
                                <th className="px-6 py-4 font-semibold text-right w-32">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading || isTypesLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-500" />
                                        <p>Memuat data pembicara...</p>
                                    </td>
                                </tr>
                            ) : filteredSpeakers.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        Data tidak ditemukan.
                                    </td>
                                </tr>
                            ) : (
                                currentSpeakers.map((speaker, index) => {
                                    const type = certificateTypes?.find((t: CertificateType) => t.id === speaker.certificate_type_id);
                                    return (
                                        <tr key={speaker.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                            <td className="px-6 py-4 text-center font-medium text-slate-500 dark:text-slate-400">
                                                {startIndex + index + 1}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-900 dark:text-white mb-1">{speaker.name}</div>
                                                {speaker.email && (
                                                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{speaker.email}</div>
                                                )}
                                                {type && (
                                                    <div className="text-xs font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400 px-2.5 py-1 rounded-md inline-flex">
                                                        {type.name}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {activeTab === "moderator" ? (
                                                    speaker.rooms && speaker.rooms.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {speaker.rooms.map((room) => (
                                                                <span key={room.id} className="text-xs font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400 px-2.5 py-1 rounded-md">
                                                                    {room.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-slate-400 italic">Belum ditugaskan ke ruangan</span>
                                                    )
                                                ) : (
                                                    <>
                                                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                                            {speaker.topic || "-"}
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-1">
                                                            {speaker.institution || "-"}
                                                        </div>
                                                    </>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <Switch 
                                                    checked={speaker.is_present}
                                                    onCheckedChange={() => handleTogglePresent(speaker.id, speaker.is_present)}
                                                    disabled={updateSpeaker.isPending}
                                                />
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        onClick={() => handleOpenModal(speaker)}
                                                        className="h-8 w-8 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        onClick={() => setDeleteConfirmId(speaker.id)}
                                                        className="h-8 w-8 text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30 dark:hover:bg-red-900/50"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Pagination */}
                {totalItems > 0 && !isLoading && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Prev</span>
                                </Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm min-w-[3rem] text-center">
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages || totalPages === 0}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Tambah/Edit */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {editingId ? "Edit Pembicara" : "Tambah Pembicara"}
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 mt-1.5">
                            Isi detail pembicara dan kaitkan dengan tipe sertifikat jika ada.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[75vh]">
                        <div className="space-y-2">
                            <Label htmlFor="period_id" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Periode <span className="text-red-500">*</span></Label>
                            <Select value={formData.period_id} onValueChange={(val) => setFormData({ ...formData, period_id: val })}>
                                <SelectTrigger className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                                    <SelectValue placeholder="Pilih Periode" />
                                </SelectTrigger>
                                <SelectContent>
                                    {periods?.map((p) => (
                                        <SelectItem key={p.period_id} value={String(p.period_id)}>
                                            {p.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Track Konferensi</Label>
                            <Select value={formData.conference_id} onValueChange={(val) => setFormData({ ...formData, conference_id: val })}>
                                <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                                    <SelectValue placeholder="Pilih Track Konferensi (Kosongkan jika Keynote)" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                    <SelectItem value="none">Utama (Keynote Speaker)</SelectItem>
                                    {conferences?.map((conf: any) => (
                                        <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>
                                            {conf.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nama Pembicara (Beserta Gelar) <span className="text-red-500">*</span></Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Prof. Dr. Fulan, M.Pd."
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="fulan@email.com"
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tipe Sertifikat (Opsional)</Label>
                            <Select value={formData.certificate_type_id} onValueChange={(val) => setFormData({ ...formData, certificate_type_id: val })}>
                                <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                                    <SelectValue placeholder="Pilih Tipe Sertifikat" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                    <SelectItem value="none">Tidak ada</SelectItem>
                                    {certificateTypes?.filter(t => [5,6,7].includes(t.id)).map((type: CertificateType) => (
                                        <SelectItem key={type.id} value={String(type.id)}>
                                            {type.name} ({type.code})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="institution" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Institusi Asal</Label>
                            <Input
                                id="institution"
                                value={formData.institution}
                                onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                                placeholder="Universitas XYZ"
                                className="h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 block w-full"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="topic" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Topik / Materi Pembicaraan</Label>
                            <Textarea
                                id="topic"
                                value={formData.topic}
                                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                                placeholder="Masa Depan Pendidikan Era 5.0"
                                className="resize-none h-20 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
                            />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                            <div className="space-y-0.5">
                                <Label className="text-sm font-bold text-slate-900 dark:text-white">Status Kehadiran</Label>
                                <p className="text-xs text-slate-500">Tandai jika pembicara telah hadir di acara.</p>
                            </div>
                            <Switch 
                                checked={formData.is_present}
                                onCheckedChange={(checked) => setFormData({ ...formData, is_present: checked })}
                            />
                        </div>

                        <DialogFooter className="pt-4 mt-6 border-t border-slate-200 dark:border-slate-800">
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="h-11 px-6 rounded-xl">
                                Batal
                            </Button>
                            <Button type="submit" disabled={createSpeaker.isPending || updateSpeaker.isPending} className="h-11 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700">
                                {(createSpeaker.isPending || updateSpeaker.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Simpan
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm Modal */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Pembicara?</DialogTitle>
                        <DialogDescription>
                            Tindakan ini tidak dapat dibatalkan.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Batal</Button>
                        <Button 
                            variant="destructive" 
                            disabled={deleteSpeaker.isPending}
                            onClick={async () => {
                                if (!deleteConfirmId) return;
                                try {
                                    await deleteSpeaker.mutateAsync(deleteConfirmId);
                                    toast.success("Pembicara berhasil dihapus!");
                                    setDeleteConfirmId(null);
                                } catch (error) {
                                    toast.error("Gagal menghapus pembicara.");
                                }
                            }}
                        >
                            {deleteSpeaker.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

